import { createSelector } from '@reduxjs/toolkit';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import ProductIntervalSelector from '@/components/products/ProductIntervalSelector';
import ProductPrice from '@/components/products/ProductPrice';
import useCheckoutOrUpgrade from '@/components/products/useCheckoutOrUpgrade';
import { formatPrice, getCurrencySymbol, parseBenefits } from '@/components/products/utils';
import useOpenSpeedyFlow from '@/components/SpeedyFlow/hooks/useOpenSpeedyFlow';
import { useDangerousLatestValueRef } from '@/hooks/useLatestValueRef';
import { getProductsQueryOptions, postUserProductMutationFn } from '@/queries/products';
import type { ParsedUserProduct, ProductInterval } from '@/queries/products/types';
import { AppState, useSelector } from '@/redux/helper';
import { trackMP } from '@/shared/mp';

import PromotedProductSection from './PromotedProductSection';
import UserProductImage from './UserProductImage';
import {
  EDUCATOR_PAIR_LOOKUP_KEYS,
  EXPLORER_PAIR_LOOKUP_KEYS,
  selectProductsMap,
  SPONSORSHIP_TIER_ONE_LOOKUP_KEYS,
  type UpsellType,
} from './utils';

interface UpsellPromotedProductSectionProps {
  upsellType: UpsellType;
  habitatId?: string;
  activeUserProducts?: ParsedUserProduct[];
  onUpgraded?: () => void;
}

const DayPassUpsellBlock = () => {
  const { data: dayPassProduct, isLoading } = useQuery({
    ...getProductsQueryOptions({ lookupKey: 'all-habitats-day-pass' }),
    select: (data) => data.list[0],
  });

  const benefits = useMemo(() => parseBenefits(dayPassProduct), [dayPassProduct]);

  const { mutate: checkout, isPending } = useMutation({
    mutationFn: postUserProductMutationFn,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  if (isLoading || !dayPassProduct) return null;

  return (
    <PromotedProductSection
      image={<UserProductImage className="size-11 shrink-0 desktop:size-[60px]" />}
      title="All-Access Pass & Guided Experience"
      subtitle="Go on an interactive expedition through the best habitats at your pace, plus 24h to explore on your own."
      priceRow={<ProductPrice currency={dayPassProduct.price.currency} price={dayPassProduct.price.price} />}
      benefitLines={benefits}
      primaryButton={{
        label: 'Select Pass',
        disabled: isPending || !dayPassProduct.eligibility?.allowed,
        loading: isPending,
        dataId: 'upsell-day-pass-button',
        onClick: () => {
          checkout({
            productId: dayPassProduct._id,
            priceId: dayPassProduct.price.priceId,
          });
          trackMP('request-checkout', {
            productId: dayPassProduct._id,
            priceId: dayPassProduct.price.priceId,
            productLookupKey: dayPassProduct.productLookupKey,
            entryPoint: 'my-purchases-upsell',
          });
        },
      }}
      helperContent="Note: Journey lengths vary — while most take around 60 minutes, you're free to go as quickly or leisurely as you like. 24-hours begin at the start of your guided journey."
    />
  );
};

const EducatorWeekPassUpsellBlock = () => {
  const { data: weekPassProduct, isLoading } = useQuery({
    ...getProductsQueryOptions({ lookupKey: 'educator-week-pass' }),
    select: (data) => data.list[0],
  });

  const benefits = useMemo(() => parseBenefits(weekPassProduct), [weekPassProduct]);

  // Week pass is single-use (never tier-changeable), so this resolves to a plain checkout.
  const { disabled, loading, onClick } = useCheckoutOrUpgrade(weekPassProduct, { entryPoint: 'my-purchases-upsell' });

  if (isLoading || !weekPassProduct) return null;

  return (
    <PromotedProductSection
      image={<UserProductImage className="size-11 shrink-0 desktop:size-[60px]" />}
      title="Lesson Pack Week Pass"
      subtitle="One-week access to zero-prep lesson pack"
      priceRow={<ProductPrice currency={weekPassProduct.price.currency} price={weekPassProduct.price.price} />}
      benefitLines={benefits}
      primaryButton={{
        label: 'Select Pass',
        disabled,
        loading,
        dataId: 'upsell-educator-week-pass-button',
        onClick,
      }}
    />
  );
};

const ExplorerMonthlyAnnualUpsellBlock = () => {
  const [interval, setInterval] = useState<ProductInterval>('month');

  const { data: productsMap, isLoading } = useQuery({
    ...getProductsQueryOptions({ lookupKey: EXPLORER_PAIR_LOOKUP_KEYS }),
    select: selectProductsMap,
  });

  const monthlyProduct = productsMap?.get('monthly-member');
  const yearlyProduct = productsMap?.get('annual-member');
  const explorerProduct = interval === 'month' ? monthlyProduct : yearlyProduct;

  const benefits = useMemo(() => parseBenefits(explorerProduct), [explorerProduct]);

  // Explorer (L3) is a fresh purchase here — plain checkout, no tierChange. Its only tier change would
  // be a downgrade from Educator (L4), which is next-cycle and handled in the managed-subscription view.
  const { mutate: checkout, isPending } = useMutation({
    mutationFn: postUserProductMutationFn,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  if (isLoading || !explorerProduct) return null;

  return (
    <PromotedProductSection
      image={<UserProductImage className="size-11 shrink-0 desktop:size-[60px]" />}
      title="Explorer Plan"
      subtitle="Unlimited discovery and delight."
      priceRow={
        <>
          <ProductPrice currency={explorerProduct.price.currency} price={explorerProduct.price.price} />
          <ProductIntervalSelector value={interval} onChange={setInterval} />
        </>
      }
      benefitLines={benefits}
      primaryButton={{
        label: 'Select Plan',
        disabled: isPending || !explorerProduct.eligibility?.allowed,
        loading: isPending,
        dataId: 'upsell-explorer-button',
        onClick: () => {
          checkout({
            productId: explorerProduct._id,
            priceId: explorerProduct.price.priceId,
          });
          trackMP('request-checkout', {
            productId: explorerProduct._id,
            priceId: explorerProduct.price.priceId,
            productLookupKey: explorerProduct.productLookupKey,
            entryPoint: 'my-purchases-upsell',
          });
        },
      }}
      helperContent="Cancel anytime."
    />
  );
};

interface EducatorMonthlyAnnualUpsellBlockProps {
  activeUserProducts?: ParsedUserProduct[];
  onUpgraded?: () => void;
}

const EducatorMonthlyAnnualUpsellBlock = ({ activeUserProducts, onUpgraded }: EducatorMonthlyAnnualUpsellBlockProps) => {
  const [interval, setInterval] = useState<ProductInterval>('month');

  const { data: productsMap, isLoading } = useQuery({
    ...getProductsQueryOptions({ lookupKey: EDUCATOR_PAIR_LOOKUP_KEYS }),
    select: selectProductsMap,
  });

  const monthlyProduct = productsMap?.get('monthly-educator');
  const yearlyProduct = productsMap?.get('annual-educator');
  const educatorProduct = interval === 'month' ? monthlyProduct : yearlyProduct;

  const benefits = useMemo(() => parseBenefits(educatorProduct), [educatorProduct]);

  // Educator (L4) can be an immediate prorated upgrade from an existing Explorer (L3) subscription.
  const { isUpgrade, isUpgradeSuccess, label, disabled, loading, onClick } = useCheckoutOrUpgrade(educatorProduct, {
    entryPoint: 'my-purchases-upsell',
  });

  // Let the "Upgraded ✔" state show briefly before closing the details modal. onUpgraded is the modal's
  // onClose, typically an inline arrow whose identity changes every render; hold it in a ref so the
  // 1.5s timer keys only off isUpgradeSuccess and isn't reset by the post-upgrade refetch re-renders.
  const onUpgradedRef = useDangerousLatestValueRef(onUpgraded);
  useEffect(() => {
    if (!isUpgradeSuccess) return undefined;
    const timeout = setTimeout(() => onUpgradedRef?.current?.(), 1500);
    return () => clearTimeout(timeout);
  }, [isUpgradeSuccess, onUpgradedRef]);

  if (isLoading || !educatorProduct) return null;

  // Nothing to promote when the user can neither buy nor upgrade (e.g. already on an Educator plan);
  // keep rendering through the success state, since the refetched eligibility no longer allows either
  if (!isUpgradeSuccess && !educatorProduct.eligibility?.allowed && !isUpgrade) return null;

  const upgradeSourceInterval = activeUserProducts?.find(
    (userProduct) => userProduct._id === educatorProduct.eligibility?.tierChangeableUserProductId,
  )?.product.price?.interval;
  const upgradeFromSentence = upgradeSourceInterval
    ? `Upgrade from your ${upgradeSourceInterval === 'year' ? 'Annual' : 'Monthly'} Explorer Plan.`
    : 'Upgrade from your Explorer Plan.';

  return (
    <PromotedProductSection
      image={<UserProductImage className="size-11 shrink-0 desktop:size-[60px]" />}
      title="Educator Plan"
      subtitle="Unlimited learning on top of existing access."
      priceRow={
        <>
          <ProductPrice currency={educatorProduct.price.currency} price={educatorProduct.price.price} />
          <ProductIntervalSelector value={interval} onChange={setInterval} />
        </>
      }
      benefitLines={benefits}
      primaryButton={{
        label,
        disabled,
        loading,
        dataId: 'upsell-educator-button',
        onClick,
      }}
      helperContent={
        isUpgradeSuccess
          ? 'Your plan has been upgraded successfully.'
          : isUpgrade
          ? `${upgradeFromSentence} Your upgraded access starts right away, and you'll only be charged the prorated difference for the rest of your current billing cycle.`
          : 'Cancel anytime.'
      }
    />
  );
};

interface SponsorshipUpsellBlockProps {
  habitatId: string;
}

const SponsorshipUpsellBlock = ({ habitatId }: SponsorshipUpsellBlockProps) => {
  const dispatch = useDispatch();
  const openSpeedyFlow = useOpenSpeedyFlow();
  const [interval, setInterval] = useState<ProductInterval>('month');

  const selectHabitat = useMemo(
    () =>
      createSelector(
        (state: AppState) => state.allHabitats,
        (allHabitats) => allHabitats.find((h) => h._id === habitatId),
      ),
    [habitatId],
  );
  const habitat = useSelector(selectHabitat);

  const { data: productsMap, isLoading } = useQuery({
    ...getProductsQueryOptions({
      habitatId,
      lookupKey: SPONSORSHIP_TIER_ONE_LOOKUP_KEYS,
    }),
    select: selectProductsMap,
  });

  const monthlyProduct = productsMap?.get('monthly-supporter-tier-1');
  const yearlyProduct = productsMap?.get('annual-supporter-tier-1');
  const tierOneProduct = interval === 'month' ? monthlyProduct : yearlyProduct;

  const benefits = useMemo(() => parseBenefits(tierOneProduct), [tierOneProduct]);

  if (isLoading || !tierOneProduct || !habitat) return null;

  return (
    <PromotedProductSection
      image={
        <UserProductImage
          className="size-11 shrink-0 desktop:size-[60px]"
          imageURL={habitat.profileImage}
          title={habitat.animal}
        />
      }
      title="Habitat Supporter"
      subtitle={`Unlimited Access to ${habitat.animal} at ${habitat.zoo.name}`}
      priceRow={
        <>
          <span className="inline-flex items-center text-2xl/6 font-medium text-secondary-green">
            From {getCurrencySymbol(tierOneProduct.price.currency)}
            {formatPrice(tierOneProduct.price.price)}
          </span>
          <ProductIntervalSelector value={interval} onChange={setInterval} />
        </>
      }
      benefitLines={benefits}
      primaryButton={{
        label: 'Purchase Plan',
        dataId: 'upsell-sponsorship-button',
        onClick: () => {
          openSpeedyFlow({
            kind: 'habitat-access',
            habitatId,
            ppt: 'sponsor',
            entryPoint: 'my-purchases-upsell',
          });
        },
      }}
      helperContent="Your upgraded access starts right away, and you'll only be charged the prorated difference for the rest of your current billing cycle."
    />
  );
};

const UpsellPromotedProductSection = ({
  upsellType,
  habitatId,
  activeUserProducts,
  onUpgraded,
}: UpsellPromotedProductSectionProps) => {
  switch (upsellType) {
    case 'day-pass':
      return <DayPassUpsellBlock />;
    case 'explorer':
      return <ExplorerMonthlyAnnualUpsellBlock />;
    case 'educator':
      return <EducatorMonthlyAnnualUpsellBlock activeUserProducts={activeUserProducts} onUpgraded={onUpgraded} />;
    case 'educator-week-pass':
      return <EducatorWeekPassUpsellBlock />;
    case 'sponsorship':
      return habitatId ? <SponsorshipUpsellBlock habitatId={habitatId} /> : null;
    default:
      return null;
  }
};

export default UpsellPromotedProductSection;
